import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { 
  FiChevronDown, 
  FiChevronUp, 
  FiFolder, 
  FiHome, 
  FiHeart, 
  FiCoffee, 
  FiTool, 
  FiShoppingBag, 
  FiAward, 
  FiInfo, 
  FiPhone, 
  FiBookOpen, 
  FiHelpCircle, 
  FiDollarSign,
  FiUserPlus
} from 'react-icons/fi';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'business' | 'company' | 'resources' | null
  const [mobileDropdown, setMobileDropdown] = useState(null); // 'business' | 'company' | 'resources' | null
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setActiveDropdown(null);
    setMobileDropdown(null);
    setMenuOpen(false);
  }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const getTriggerClass = (dropdownName) => {
    const isActive = activeDropdown === dropdownName;
    if (isActive) {
      return 'flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-gray-900 text-white rounded-full transition-all duration-200 cursor-pointer select-none';
    }
    if (scrolled) {
      return 'flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-gray-600 hover:text-payday-blue hover:bg-gray-50 rounded-full transition-all duration-200 cursor-pointer select-none';
    } else {
      return 'flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-white/90 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 cursor-pointer select-none';
    }
  };

  const getLinkClass = (isActiveLink) => {
    if (scrolled) {
      return `text-sm font-bold px-3 py-2 rounded-full transition-all duration-200 ${
        isActiveLink ? 'text-payday-blue bg-blue-50/50' : 'text-gray-600 hover:text-payday-blue hover:bg-gray-50'
      }`;
    } else {
      return `text-sm font-bold px-3 py-2 rounded-full transition-all duration-200 ${
        isActiveLink ? 'text-pto-yellow bg-white/10' : 'text-white/90 hover:text-white hover:bg-white/10'
      }`;
    }
  };

  const businessTypes = [
    { name: 'Accounting & bookkeeping', desc: 'Built around your client workflow', icon: FiFolder },
    { name: 'Small business', desc: 'Less admin, more business', icon: FiHome },
    { name: 'Healthcare', desc: 'Simple payroll for busy clinics', icon: FiHeart },
    { name: 'Restaurants & hospitality', desc: 'Shifts, tips and patio season', icon: FiCoffee },
    { name: 'Construction & trades', desc: 'Crews, contracts and seasonal work', icon: FiTool },
    { name: 'Retail', desc: 'Seasonal staff and store-floor payroll', icon: FiShoppingBag },
    { name: 'Law firms', desc: 'Salaried staff and partner pay, sorted', icon: FiAward },
  ];

  const companyItems = [
    { name: 'About Us', desc: 'Learn about our mission, vision and values', icon: FiInfo, to: '/about' },
    { name: 'Contact Us', desc: 'Get in touch with our friendly support', icon: FiPhone, to: '/contact' },
  ];

  const resourceItems = [
    { name: 'Blog', desc: 'Payroll tips, tax updates, and HR guides', icon: FiBookOpen, to: '/pricing' },
    { name: 'Help Center', desc: 'Self-serve documentation and tutorials', icon: FiHelpCircle, to: '/pricing' },
    { name: 'Payroll Calculator', desc: 'Calculate deductions and take-home pay', icon: FiDollarSign, to: '/pricing' },
    { name: 'Onboarding', desc: 'Create your OrientalPay account with us', icon: FiUserPlus, to: '/onboarding' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-md border-b border-gray-100 py-2' 
          : 'bg-transparent py-3'
      }`} 
      ref={navRef}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-12">

        {/* Logo */}
        <NavLink to="/">
          <img 
            src="/Logo.png" 
            alt="Oriental Business Solutions" 
            className={`h-10 w-auto object-contain transition-all duration-300 ${
              scrolled ? '' : 'brightness-0 invert'
            }`} 
          />
        </NavLink>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-4 list-none m-0 p-0 relative">
          
          {/* Business Type Dropdown */}
          <li className="relative">
            <button 
              onClick={() => toggleDropdown('business')}
              className={getTriggerClass('business')}
            >
              <span>Business type</span>
              {activeDropdown === 'business' ? <FiChevronUp className="text-xs" /> : <FiChevronDown className="text-xs" />}
            </button>

            {/* Mega Menu Dropdown */}
            {activeDropdown === 'business' && (
              <div className="absolute top-full left-0 mt-3 w-[680px] bg-white rounded-3xl shadow-2xl border border-gray-100 p-6 z-50 animate-fade-in">
                <div className="text-gray-900 font-extrabold text-base mb-4 px-2">Business type</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {businessTypes.map((item, idx) => {
                    const IconComponent = item.icon;
                    return (
                      <Link 
                        key={idx}
                        to="/contact"
                        className="flex items-start gap-4 p-2.5 rounded-2xl hover:bg-blue-50/50 transition-all duration-200 group"
                      >
                        <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center text-payday-blue text-lg flex-shrink-0 group-hover:bg-payday-blue group-hover:text-white transition-all duration-200">
                          <IconComponent />
                        </div>
                        <div>
                          <div className="text-gray-900 font-bold text-sm group-hover:text-payday-blue transition-colors">{item.name}</div>
                          <div className="text-gray-500 text-xs mt-0.5 leading-relaxed">{item.desc}</div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </li>

          {/* Services */}
          <li>
            <NavLink to="/products" className={({ isActive }) => getLinkClass(isActive)}>
              Services
            </NavLink>
          </li>

          {/* Pricing */}
          <li>
            <NavLink to="/pricing" className={({ isActive }) => getLinkClass(isActive)}>
              Pricing
            </NavLink>
          </li>

          {/* Company Dropdown */}
          <li className="relative">
            <button 
              onClick={() => toggleDropdown('company')}
              className={getTriggerClass('company')}
            >
              <span>Company</span>
              {activeDropdown === 'company' ? <FiChevronUp className="text-xs" /> : <FiChevronDown className="text-xs" />}
            </button>

            {/* List Dropdown */}
            {activeDropdown === 'company' && (
              <div className="absolute top-full left-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 px-2 z-50 animate-fade-in">
                {companyItems.map((item, idx) => {
                  const IconComponent = item.icon;
                  return (
                    <Link 
                      key={idx}
                      to={item.to}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-blue-50/50 transition-all duration-200 group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-payday-blue text-sm flex-shrink-0 group-hover:bg-payday-blue group-hover:text-white transition-all duration-200">
                        <IconComponent />
                      </div>
                      <div>
                        <div className="text-gray-900 font-bold text-sm group-hover:text-payday-blue transition-colors">{item.name}</div>
                        <div className="text-gray-500 text-xs mt-0.5 leading-normal">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </li>

          {/* Resources Dropdown */}
          <li className="relative">
            <button 
              onClick={() => toggleDropdown('resources')}
              className={getTriggerClass('resources')}
            >
              <span>Resources</span>
              {activeDropdown === 'resources' ? <FiChevronUp className="text-xs" /> : <FiChevronDown className="text-xs" />}
            </button>

            {/* List Dropdown */}
            {activeDropdown === 'resources' && (
              <div className="absolute top-full left-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 py-3 px-2 z-50 animate-fade-in">
                {resourceItems.map((item, idx) => {
                  const IconComponent = item.icon;
                  return (
                    <Link 
                      key={idx}
                      to={item.to}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-blue-50/50 transition-all duration-200 group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-payday-blue text-sm flex-shrink-0 group-hover:bg-payday-blue group-hover:text-white transition-all duration-200">
                        <IconComponent />
                      </div>
                      <div>
                        <div className="text-gray-900 font-bold text-sm group-hover:text-payday-blue transition-colors">{item.name}</div>
                        <div className="text-gray-500 text-xs mt-0.5 leading-normal">{item.desc}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </li>

        </ul>

        {/* CTA buttons */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href="https://portal.orientalpay.ca/"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm font-bold transition-colors duration-200 ${scrolled ? 'text-gray-600 hover:text-payday-blue' : 'text-white/90 hover:text-white'}`}>
            Log In
          </a>
          <NavLink to="/contact">
            <button className="px-6 py-2.5 text-sm btn-pto-yellow">
              Book a Demo
            </button>
          </NavLink>
          <NavLink to="/pricing">
            <button className="px-6 py-2.5 text-sm btn-payday-blue">
              Start Free Trial
            </button>
          </NavLink>
        </div>

        {/* Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 p-2"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`w-6 h-0.5 rounded transition-all ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
          <span className={`w-6 h-0.5 rounded transition-all ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${menuOpen ? 'opacity-0' : ''}`} />
          <span className={`w-6 h-0.5 rounded transition-all ${
            scrolled ? 'bg-gray-800' : 'bg-white'
          } ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-5 flex flex-col gap-4 shadow-lg max-h-[85vh] overflow-y-auto">
          
          {/* Business Type Mobile Accordion */}
          <div className="flex flex-col border-b border-gray-50 pb-2">
            <button 
              onClick={() => setMobileDropdown(mobileDropdown === 'business' ? null : 'business')}
              className="flex items-center justify-between text-sm font-bold text-gray-700 hover:text-payday-blue py-2 w-full text-left"
            >
              <span>Business type</span>
              {mobileDropdown === 'business' ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {mobileDropdown === 'business' && (
              <div className="flex flex-col gap-3 pl-4 pt-2 pb-1 bg-gray-50/50 rounded-xl mt-1">
                {businessTypes.map((item, idx) => (
                  <Link 
                    key={idx} 
                    to="/contact" 
                    onClick={() => { setMenuOpen(false); setMobileDropdown(null); }}
                    className="flex items-center gap-3 py-1.5 text-xs text-gray-600 hover:text-payday-blue font-semibold"
                  >
                    <item.icon className="text-payday-blue text-sm flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Services */}
          <NavLink
            to="/products"
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `text-sm font-bold transition-colors py-2 border-b border-gray-50 ${isActive ? 'text-payday-blue' : 'text-gray-700 hover:text-payday-blue'}`
            }
          >
            Services
          </NavLink>

          {/* Pricing */}
          <NavLink
            to="/pricing"
            onClick={() => setMenuOpen(false)}
            className={({ isActive }) =>
              `text-sm font-bold transition-colors py-2 border-b border-gray-50 ${isActive ? 'text-payday-blue' : 'text-gray-700 hover:text-payday-blue'}`
            }
          >
            Pricing
          </NavLink>
          {/* Company Mobile Accordion */}
          <div className="flex flex-col border-b border-gray-50 pb-2">
            <button 
              onClick={() => setMobileDropdown(mobileDropdown === 'company' ? null : 'company')}
              className="flex items-center justify-between text-sm font-bold text-gray-700 hover:text-payday-blue py-2 w-full text-left"
            >
              <span>Company</span>
              {mobileDropdown === 'company' ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {mobileDropdown === 'company' && (
              <div className="flex flex-col gap-3 pl-4 pt-2 pb-1 bg-gray-50/50 rounded-xl mt-1">
                {companyItems.map((item, idx) => (
                  <Link 
                    key={idx} 
                    to={item.to}
                    onClick={() => { setMenuOpen(false); setMobileDropdown(null); }}
                    className="flex items-center gap-3 py-1.5 text-xs text-gray-600 hover:text-payday-blue font-semibold"
                  >
                    <item.icon className="text-payday-blue text-sm flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Resources Mobile Accordion */}
          <div className="flex flex-col border-b border-gray-50 pb-2">
            <button 
              onClick={() => setMobileDropdown(mobileDropdown === 'resources' ? null : 'resources')}
              className="flex items-center justify-between text-sm font-bold text-gray-700 hover:text-payday-blue py-2 w-full text-left"
            >
              <span>Resources</span>
              {mobileDropdown === 'resources' ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {mobileDropdown === 'resources' && (
              <div className="flex flex-col gap-3 pl-4 pt-2 pb-1 bg-gray-50/50 rounded-xl mt-1">
                {resourceItems.map((item, idx) => (
                  <Link 
                    key={idx} 
                    to={item.to}
                    onClick={() => { setMenuOpen(false); setMobileDropdown(null); }}
                    className="flex items-center gap-3 py-1.5 text-xs text-gray-600 hover:text-payday-blue font-semibold"
                  >
                    <item.icon className="text-payday-blue text-sm flex-shrink-0" />
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Mobile CTA buttons */}
          <div className="flex flex-col gap-3 pt-3 border-t border-gray-100">
            <a
              href="https://portal.orientalpay.ca/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-2.5 text-sm font-bold text-gray-600 hover:text-payday-blue transition-colors text-center">Log In</a>
            <NavLink to="/contact" onClick={() => setMenuOpen(false)}>
              <button className="w-full py-2.5 text-sm btn-pto-yellow">Book a Demo</button>
            </NavLink>
            <NavLink to="/pricing" onClick={() => setMenuOpen(false)}>
              <button className="w-full py-2.5 text-sm btn-payday-blue">Start Free Trial</button>
            </NavLink>
          </div>
        </div>
      )}
    </nav>
  );
}